import { useEffect } from 'react';
import usePagination from './usePagination';
import { IAsset } from '../models/Assets';
import { indexAllAssets } from 'api/its/assets';
import { its_access } from './useAssets';

type Props = {
    projects_ids?: number[]
    type_id?: number;
    pageSize?: number;
    assets_base_line_id?: number;
    check_status?: 'Verificado' | 'Parcialmente Verificado' | 'Sin Verificar';
    segment_id?: number;
}

const useAllAssets = ({
    projects_ids = [45, 46, 47, 48, 49, 50],
    type_id,
    pageSize: defaultPageSize = 1200,
    assets_base_line_id,
    check_status,
    segment_id
}: Props): ReturnType<typeof usePagination<IAsset>> => {
    const hookAllAssets = usePagination<IAsset>({ pageSize: defaultPageSize });
    const { page, pageSize } = hookAllAssets;

    useEffect(() => {
        const getAssetsByIds = async () => {
            hookAllAssets.loadingStart();
            const { data, totalItems }: { data: IAsset[]; totalItems: number } = await indexAllAssets({
                table: 'assets_its',
                query: {
                    projects_ids,
                    page,
                    pageSize,
                    type_id,
                    assets_base_line_id,
                    check_status,
                    segment_id,
                    orderBy: 'description:asc'
                }
            });
            let processedData: IAsset[] = [];
            if (Array.isArray(data)) {
                processedData = data.map((item) => {
                    const element_its = its_access.find(item_its => item_its.id === item.id);
                    return {
                        ...item,
                        url: element_its ? element_its.url : undefined,
                        rtspUrl: element_its ? element_its.rtspUrl : undefined,
                        xaddr: element_its ? element_its.xaddr : undefined,
                        user: element_its ? element_its.user : undefined,
                        pass: element_its ? element_its.pass : undefined,
                        offline: element_its ? element_its.offline : undefined,
                    }
                }).sort((a, b) => {
                    // Extraer el número de kilómetros y la letra de la descripción
                    const getAssetInfo = (desc: string | null) => {
                        if (!desc) return { km: 0, letter: '' };
                        // Primero intentamos el formato con letra
                        const matchWithLetter = desc.match(/([A-Z]+) (\d+)\+(\d+) ([A-Z])/);
                        if (matchWithLetter) {
                            return {
                                km: parseInt(matchWithLetter[2]),
                                letter: matchWithLetter[4]
                            };
                        }
                        // Si no tiene letra, intentamos el formato simple
                        const matchSimple = desc.match(/([A-Z]+) (\d+)\+(\d+)/);
                        if (matchSimple) {
                            return {
                                km: parseInt(matchSimple[2]),
                                letter: 'Z' // Asignamos 'Z' para que aparezcan al final
                            };
                        }
                        return { km: 0, letter: '' };
                    };

                    const infoA = getAssetInfo(a.description);
                    const infoB = getAssetInfo(b.description);

                    // Primero ordenar por kilómetros
                    if (infoA.km !== infoB.km) {
                        return infoA.km - infoB.km;
                    }

                    // Si los kilómetros son iguales, ordenar por letra
                    if (infoA.letter !== infoB.letter) {
                        return infoA.letter.localeCompare(infoB.letter);
                    }

                    // Si todo es igual, ordenar por la descripción completa
                    return (a.description || '').localeCompare(b.description || '');
                });
            }
            hookAllAssets.setData(processedData);
            hookAllAssets.setTotalItems(totalItems || 0);
            hookAllAssets.loadingEnd();
        }
        getAssetsByIds();
    }, [page, pageSize, type_id, check_status, segment_id, assets_base_line_id]);

    return hookAllAssets;
};

export default useAllAssets;